import { useState, type CSSProperties } from 'react';
import { LayoutGrid } from 'lucide-react';
import type { CmsCategory } from '@/lib/cms-types';
import { HOME_PAGE_CATEGORY_COLOR_CLASSES } from '@/lib/help-center-home-categories';
import { getPowerCategoryAccent } from '@/lib/power-category-theme';
import { getPowerCategoryLabel } from '@/lib/powers-data';
import { cn } from '@/lib/utils';

export type PainelCategoryTone = {
  dot: string;
  bubble: string;
  bubbleActive: string;
  chip: string;
  chipActive: string;
  badge: string;
};

const CHIP_TRANSITION =
  'transition-[background-color,border-color,box-shadow,color,opacity] duration-200 ease-out';

const ALL_TONE: PainelCategoryTone = {
  dot: 'bg-sky-300',
  bubble: 'bg-sky-500/12 ring-1 ring-sky-400/15',
  bubbleActive: 'bg-sky-400/25 ring-1 ring-sky-300/30',
  chip: cn(
    'border-white/[0.08] bg-white/[0.03] text-slate-300',
    'hover:border-sky-400/30 hover:bg-sky-500/10 hover:text-sky-50'
  ),
  chipActive:
    'border-sky-400/40 bg-gradient-to-br from-sky-500/20 to-sky-600/10 text-sky-50 shadow-[0_0_20px_-6px_rgba(56,189,248,0.45)]',
  badge: 'border-sky-400/40 bg-sky-500/15 text-sky-200',
};


/** Tons escuros do painel por chave de cor das categorias de guias. */
export const PAINEL_GUIDE_CATEGORY_TONES: Record<string, PainelCategoryTone> = {
  indigo: {
    dot: 'bg-indigo-300',
    bubble: 'bg-indigo-500/14 ring-indigo-400/20',
    bubbleActive: 'bg-indigo-400/28 ring-indigo-300/35',
    chip: 'border-indigo-400/20 bg-indigo-500/8 text-indigo-200 hover:border-indigo-400/40 hover:bg-indigo-500/14 hover:text-indigo-50',
    chipActive:
      'border-indigo-400/45 bg-gradient-to-br from-indigo-500/22 to-indigo-600/10 text-indigo-50 shadow-[0_0_20px_-6px_rgba(99,102,241,0.5)]',
    badge: 'border-indigo-400/40 bg-indigo-500/15 text-indigo-200',
  },
  blue: {
    dot: 'bg-blue-300',
    bubble: 'bg-blue-500/14 ring-blue-400/20',
    bubbleActive: 'bg-blue-400/28 ring-blue-300/35',
    chip: 'border-blue-400/20 bg-blue-500/8 text-blue-200 hover:border-blue-400/40 hover:bg-blue-500/14 hover:text-blue-50',
    chipActive:
      'border-blue-400/45 bg-gradient-to-br from-blue-500/22 to-blue-600/10 text-blue-50 shadow-[0_0_20px_-6px_rgba(59,130,246,0.5)]',
    badge: 'border-blue-400/40 bg-blue-500/15 text-blue-200',
  },
  emerald: {
    dot: 'bg-emerald-300',
    bubble: 'bg-emerald-500/14 ring-emerald-400/20',
    bubbleActive: 'bg-emerald-400/28 ring-emerald-300/35',
    chip: 'border-emerald-400/20 bg-emerald-500/8 text-emerald-200 hover:border-emerald-400/40 hover:bg-emerald-500/14 hover:text-emerald-50',
    chipActive:
      'border-emerald-400/45 bg-gradient-to-br from-emerald-500/22 to-emerald-600/10 text-emerald-50 shadow-[0_0_20px_-6px_rgba(16,185,129,0.45)]',
    badge: 'border-emerald-400/40 bg-emerald-500/15 text-emerald-200',
  },
  amber: {
    dot: 'bg-amber-300',
    bubble: 'bg-amber-500/14 ring-amber-400/20',
    bubbleActive: 'bg-amber-400/28 ring-amber-300/35',
    chip: 'border-amber-400/20 bg-amber-500/8 text-amber-200 hover:border-amber-400/40 hover:bg-amber-500/14 hover:text-amber-50',
    chipActive:
      'border-amber-400/45 bg-gradient-to-br from-amber-500/22 to-amber-600/10 text-amber-50 shadow-[0_0_20px_-6px_rgba(245,158,11,0.45)]',
    badge: 'border-amber-400/40 bg-amber-500/15 text-amber-200',
  },
  sky: {
    dot: 'bg-sky-300',
    bubble: 'bg-sky-500/14 ring-sky-400/20',
    bubbleActive: 'bg-sky-400/28 ring-sky-300/35',
    chip: 'border-sky-400/20 bg-sky-500/8 text-sky-200 hover:border-sky-400/40 hover:bg-sky-500/14 hover:text-sky-50',
    chipActive:
      'border-sky-400/45 bg-gradient-to-br from-sky-500/22 to-sky-600/10 text-sky-50 shadow-[0_0_20px_-6px_rgba(56,189,248,0.45)]',
    badge: 'border-sky-400/40 bg-sky-500/15 text-sky-200',
  },
  violet: {
    dot: 'bg-violet-300',
    bubble: 'bg-violet-500/14 ring-violet-400/20',
    bubbleActive: 'bg-violet-400/28 ring-violet-300/35',
    chip: 'border-violet-400/20 bg-violet-500/8 text-violet-200 hover:border-violet-400/40 hover:bg-violet-500/14 hover:text-violet-50',
    chipActive:
      'border-violet-400/45 bg-gradient-to-br from-violet-500/22 to-violet-600/10 text-violet-50 shadow-[0_0_20px_-6px_rgba(139,92,246,0.45)]',
    badge: 'border-violet-400/40 bg-violet-500/15 text-violet-200',
  },
  rose: {
    dot: 'bg-rose-300',
    bubble: 'bg-rose-500/14 ring-rose-400/20',
    bubbleActive: 'bg-rose-400/28 ring-rose-300/35',
    chip: 'border-rose-400/20 bg-rose-500/8 text-rose-200 hover:border-rose-400/40 hover:bg-rose-500/14 hover:text-rose-50',
    chipActive:
      'border-rose-400/45 bg-gradient-to-br from-rose-500/22 to-rose-600/10 text-rose-50 shadow-[0_0_20px_-6px_rgba(244,63,94,0.45)]',
    badge: 'border-rose-400/40 bg-rose-500/15 text-rose-200',
  },
  cyan: {
    dot: 'bg-cyan-300',
    bubble: 'bg-cyan-500/14 ring-cyan-400/20',
    bubbleActive: 'bg-cyan-400/28 ring-cyan-300/35',
    chip: 'border-cyan-400/20 bg-cyan-500/8 text-cyan-200 hover:border-cyan-400/40 hover:bg-cyan-500/14 hover:text-cyan-50',
    chipActive:
      'border-cyan-400/45 bg-gradient-to-br from-cyan-500/22 to-cyan-600/10 text-cyan-50 shadow-[0_0_20px_-6px_rgba(6,182,212,0.45)]',
    badge: 'border-cyan-400/40 bg-cyan-500/15 text-cyan-200',
  },
  fuchsia: {
    dot: 'bg-fuchsia-300',
    bubble: 'bg-fuchsia-500/14 ring-fuchsia-400/20',
    bubbleActive: 'bg-fuchsia-400/28 ring-fuchsia-300/35',
    chip: 'border-fuchsia-400/20 bg-fuchsia-500/8 text-fuchsia-200 hover:border-fuchsia-400/40 hover:bg-fuchsia-500/14 hover:text-fuchsia-50',
    chipActive:
      'border-fuchsia-400/45 bg-gradient-to-br from-fuchsia-500/22 to-fuchsia-600/10 text-fuchsia-50 shadow-[0_0_20px_-6px_rgba(217,70,239,0.45)]',
    badge: 'border-fuchsia-400/40 bg-fuchsia-500/15 text-fuchsia-200',
  },
};

const FALLBACK_GUIDE_TONE = PAINEL_GUIDE_CATEGORY_TONES.indigo;

export function resolveGuideCategoryTone(category?: CmsCategory | null): PainelCategoryTone {
  const key =
    category?.color && HOME_PAGE_CATEGORY_COLOR_CLASSES[category.color] ? category.color : 'indigo';
  return PAINEL_GUIDE_CATEGORY_TONES[key] ?? FALLBACK_GUIDE_TONE;
}

/** Estilo unificado de accent hex (filtros + badges da lista de powers). */
export function powerCategoryAccentStyle(
  accentHex: string,
  variant: 'chip' | 'chipActive' | 'chipHover' | 'badge' | 'bubble' | 'bubbleActive'
): CSSProperties {
  if (variant === 'chipActive') {
    return {
      background: `linear-gradient(135deg, ${accentHex}28 0%, ${accentHex}12 100%)`,
      borderColor: `${accentHex}70`,
      color: `color-mix(in srgb, ${accentHex} 35%, white)`,
      boxShadow: `0 0 22px -6px ${accentHex}55, inset 0 1px 0 rgba(255,255,255,0.06)`,
    };
  }
  if (variant === 'chipHover') {
    return {
      backgroundColor: `${accentHex}18`,
      borderColor: `${accentHex}50`,
      color: `color-mix(in srgb, ${accentHex} 55%, white)`,
    };
  }
  if (variant === 'bubble') {
    return {
      backgroundColor: `${accentHex}1a`,
      boxShadow: `inset 0 0 0 1px ${accentHex}30`,
    };
  }
  if (variant === 'bubbleActive') {
    return {
      backgroundColor: `${accentHex}32`,
      boxShadow: `inset 0 0 0 1px ${accentHex}55`,
    };
  }
  if (variant === 'badge') {
    return {
      backgroundColor: `${accentHex}22`,
      borderColor: `${accentHex}55`,
      color: `color-mix(in srgb, ${accentHex} 78%, white)`,
    };
  }
  return {
    backgroundColor: `${accentHex}0c`,
    borderColor: `${accentHex}28`,
    color: `color-mix(in srgb, ${accentHex} 65%, white)`,
  };
}

export function PainelGuideCategoryBadge({
  category,
  className,
}: {
  category?: CmsCategory | null;
  className?: string;
}) {
  if (!category) {
    return <span className={cn('font-inter text-sm text-slate-500', className)}>—</span>;
  }

  const tone = resolveGuideCategoryTone(category);

  return (
    <span
      className={cn(
        'inline-flex max-w-[200px] items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-poppins text-[11px] font-semibold',
        tone.badge,
        className
      )}
    >
      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', tone.dot)} aria-hidden />
      <span className="truncate">{category.name}</span>
    </span>
  );
}

export function PainelPowerCategoryBadge({
  categoryId,
  className,
}: {
  categoryId: string;
  className?: string;
}) {
  const label = getPowerCategoryLabel(categoryId);
  const accentHex = getPowerCategoryAccent(categoryId).hex;

  return (
    <span
      className={cn(
        'inline-flex max-w-[200px] items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-poppins text-[11px] font-semibold',
        className
      )}
      style={powerCategoryAccentStyle(accentHex, 'badge')}
    >
      <span
        className="h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: accentHex }}
        aria-hidden
      />
      <span className="truncate">{label}</span>
    </span>
  );
}

export type PainelCategoryFilterOption = {
  id: string;
  label: string;
  count: number;
  tone?: PainelCategoryTone;
  accentHex?: string;
};

export function PainelCategoryFilterBar({
  options,
  value,
  onChange,
  allCount,
  className,
}: {
  options: PainelCategoryFilterOption[];
  value: string | null;
  onChange: (id: string | null) => void;
  allCount: number;
  className?: string;
}) {
  const allActive = value === null;
  const activeLabel = value
    ? (options.find((o) => o.id === value)?.label ?? null)
    : null;

  return (
    <section
      className={cn(
        'relative overflow-hidden rounded-2xl border border-sky-400/15',
        'bg-gradient-to-br from-blue-950/70 via-[#0a1230]/95 to-blue-950/80',
        'px-4 py-4 shadow-[0_0_44px_-14px_rgba(56,189,248,0.22)]',
        className
      )}
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-400/35 to-transparent"
        aria-hidden
      />

      <div className="mb-3.5 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-poppins text-xs font-semibold tracking-wide text-sky-100/90">
            Filtrar por categoria
          </p>
          <p className="mt-0.5 font-inter text-[11px] text-slate-500">
            {activeLabel ? (
              <>
                A mostrar: <span className="text-sky-200/90">{activeLabel}</span>
              </>
            ) : (
              'Todas as categorias visíveis'
            )}
          </p>
        </div>
        {value ? (
          <button
            type="button"
            onClick={() => onChange(null)}
            className={cn(
              'rounded-lg border border-white/10 bg-white/[0.04] px-2.5 py-1',
              'font-inter text-[11px] font-medium text-slate-400',
              CHIP_TRANSITION,
              'hover:border-sky-400/25 hover:bg-sky-500/10 hover:text-sky-100'
            )}
          >
            Limpar filtro
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <FilterChip
          label="Todas"
          count={allCount}
          active={allActive}
          tone={ALL_TONE}
          showGridIcon
          onClick={() => onChange(null)}
        />
        {options.map((opt) => (
          <FilterChip
            key={opt.id}
            label={opt.label}
            count={opt.count}
            active={value === opt.id}
            tone={opt.tone}
            accentHex={opt.accentHex}
            onClick={() => onChange(opt.id)}
          />
        ))}
      </div>
    </section>
  );
}

function FilterChip({
  label,
  count,
  active,
  tone,
  accentHex,
  showGridIcon,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  tone?: PainelCategoryTone;
  accentHex?: string;
  showGridIcon?: boolean;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const useInline = Boolean(accentHex);

  const chipStyle = accentHex
    ? powerCategoryAccentStyle(
        accentHex,
        active ? 'chipActive' : hovered ? 'chipHover' : 'chip'
      )
    : undefined;

  const bubbleStyle = accentHex
    ? powerCategoryAccentStyle(accentHex, active ? 'bubbleActive' : 'bubble')
    : undefined;

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={chipStyle}
      className={cn(
        'group inline-flex items-center gap-2.5 rounded-xl border px-2.5 py-2',
        CHIP_TRANSITION,
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/35 focus-visible:ring-offset-0',
        !useInline && (active ? tone?.chipActive : tone?.chip)
      )}
    >
      <span
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg',
          CHIP_TRANSITION,
          !useInline && (active ? tone?.bubbleActive : tone?.bubble),
          !active && !useInline && 'group-hover:brightness-110'
        )}
        style={bubbleStyle}
      >
        {showGridIcon ? (
          <LayoutGrid
            className={cn('h-3.5 w-3.5', active ? 'text-sky-200' : 'text-slate-400 group-hover:text-sky-200/80')}
            aria-hidden
          />
        ) : tone?.dot ? (
          <span className={cn('h-2 w-2 rounded-full', tone.dot)} aria-hidden />
        ) : accentHex ? (
          <span
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: accentHex }}
            aria-hidden
          />
        ) : null}
      </span>

      <span className="whitespace-nowrap font-poppins text-[13px] font-medium leading-none tracking-tight">
        {label}
      </span>

      <span
        className={cn(
          'rounded-md px-1.5 py-0.5 font-poppins text-[10px] font-bold tabular-nums leading-none',
          CHIP_TRANSITION,
          active
            ? 'bg-black/25 text-inherit'
            : 'bg-black/15 text-inherit/75 group-hover:bg-black/20 group-hover:text-inherit'
        )}
      >
        {count}
      </span>
    </button>
  );
}
